import type { PiiType, TextDetection } from './types';

const overlaps = (a: TextDetection, b: TextDetection): boolean =>
  a.start < b.end && b.start < a.end;

/** True if `outer` fully covers `inner` — i.e. every character of
 *  `inner` lies inside `outer`. Used to drop redundant entries when
 *  one detection sits entirely within another (the wider one already
 *  paints the contained one's pixels). */
const containsFully = (outer: TextDetection, inner: TextDetection): boolean =>
  outer.start <= inner.start && outer.end >= inner.end;

const length = (d: TextDetection): number => d.end - d.start;

/**
 * Types where regex is the source of truth — structured patterns regex
 * catches reliably and NER often mis-tags as part of a name or address.
 */
const REGEX_PREFERRED: ReadonlySet<PiiType> = new Set([
  'email',
  'phone',
  'ssn',
  'ein',
  'credit_card',
  'iban',
  'ipv4',
  'ipv6',
  'api_key',
  'vin',
  'routing_number',
  'license_plate',
  // The regex address and ZIP recognisers require strong context (house
  // number + road suffix; state code + 5 digits; bracketed-label form
  // fields). When NER's "address" span overlaps one of these, dropping
  // the regex hit costs us coverage: NER's spans regularly miss the
  // ZIP at the tail end, or trim the unit specifier off an address,
  // and the regex span is the correct bound. shouldCoexist keeps both
  // when an NER span overlaps a regex-preferred type.
  'us_zip',
  'address',
  // The names dictionary requires Title-Case + first OR last in the
  // curated list, so its spans are tight by construction — exactly two
  // or three tokens. NER name spans often run on into trailing words
  // ("Sean Carter and the Landlord") because the model sees those as
  // part of the entity. Trust the dict span when both hit.
  'name',
]);

/**
 * Types where the NER model is the source of truth — free-form spans regex
 * can't reasonably catch.
 */
const NER_PREFERRED: ReadonlySet<PiiType> = new Set([
  'name',
  'address',
  'drivers_license',
  'passport',
  'dob',
]);

const isRegexPreferred = (d: TextDetection): boolean => REGEX_PREFERRED.has(d.type);
const isNerPreferred = (d: TextDetection): boolean => NER_PREFERRED.has(d.type);

const isAuthoritative = (d: TextDetection): boolean =>
  // Form-field detections are always authoritative — the label tells us
  // exactly what the value is, no inference required.
  d.source === 'form' ||
  (d.source === 'regex' && isRegexPreferred(d)) ||
  (d.source === 'ner' && isNerPreferred(d));

/**
 * When a high-precision structured hit (Luhn-verified card, email syntax,
 * form-field label) overlaps with a free-form NER span (which tends to
 * bleed into adjacent tokens), dropping either loses real coverage:
 * the structured hit verifies the value at the character level, but the
 * NER span often extends into a wider name/address that's also real PII.
 * Keep both — the redaction overlay will just paint the union, and the
 * sidebar will list both so the user can untoggle independently.
 */
const shouldCoexist = (a: TextDetection, b: TextDetection): boolean => {
  const aPrecise = a.source === 'form' || (a.source === 'regex' && isRegexPreferred(a));
  const bPrecise = b.source === 'form' || (b.source === 'regex' && isRegexPreferred(b));
  return (aPrecise && b.source === 'ner') || (bPrecise && a.source === 'ner');
};

/** Confidence delta required to override the wider-span tiebreak when
 *  both detections are authoritative. Below this we trust the wider
 *  redaction; above it we trust the more confident classifier. Tuned so
 *  a checksum-validated regex hit (0.95) clearly beats a loose NER span
 *  (~0.75) but loses to a near-equal confidence (0.92). */
const CONFIDENCE_MARGIN = 0.1;

/**
 * Pick the better of two overlapping detections.
 * Cross-source rule (the kickoff's contract):
 *   - regex wins for structured types (email, SSN, credit card, ...)
 *   - NER wins for free-form types (name, address, DL, passport, DOB)
 * When only one detection's type matches its source's authoritative set,
 * that detection wins. When both are authoritative for their own type,
 * prefer the markedly more confident detection (so a Luhn-validated card
 * isn't dropped for a loose NER address that happens to span more
 * characters). Within the margin, fall back to the wider span — we'd
 * rather redact too much than too little. Otherwise fall back to
 * confidence > length > earlier start.
 */
const better = (a: TextDetection, b: TextDetection): TextDetection => {
  const aAuth = isAuthoritative(a);
  const bAuth = isAuthoritative(b);
  if (aAuth && !bAuth) return a;
  if (bAuth && !aAuth) return b;
  if (aAuth && bAuth) {
    // Form-field detections have the tightest possible bound — the
    // value came from a labelled widget annotation, not inferred from
    // surrounding text — so they win any same-type overlap.
    if (a.source === 'form' && b.source !== 'form') return a;
    if (b.source === 'form' && a.source !== 'form') return b;
    // When a precise regex hit (regex-preferred type) overlaps an NER
    // span, prefer the regex span. NER's bert-base-NER spans routinely
    // bleed into adjacent tokens, picking up label words or trailing
    // punctuation. The regex pattern matched a known structured shape
    // and is the more conservative bound. This kicks in for the
    // common "regex caught the address correctly; NER widens it onto
    // the surrounding sentence" pattern users were seeing.
    if (a.source === 'regex' && isRegexPreferred(a) && b.source === 'ner') return a;
    if (b.source === 'regex' && isRegexPreferred(b) && a.source === 'ner') return b;
    if (Math.abs(a.confidence - b.confidence) > CONFIDENCE_MARGIN) {
      return a.confidence > b.confidence ? a : b;
    }
    if (length(a) !== length(b)) return length(a) > length(b) ? a : b;
    if (a.source === 'regex' && b.source !== 'regex') return a;
    if (b.source === 'regex' && a.source !== 'regex') return b;
  }
  if (a.confidence !== b.confidence) return a.confidence > b.confidence ? a : b;
  if (length(a) !== length(b)) return length(a) > length(b) ? a : b;
  return a.start <= b.start ? a : b;
};

/**
 * Dedupe overlapping detections; non-overlapping detections are preserved.
 */
export const mergeDetections = (detections: TextDetection[]): TextDetection[] => {
  if (detections.length === 0) return [];

  const sorted = [...detections].sort((a, b) => a.start - b.start || a.end - b.end);
  const out: TextDetection[] = [];

  for (const d of sorted) {
    const lastIdx = out.length - 1;
    const last = lastIdx >= 0 ? out[lastIdx] : undefined;
    if (last && overlaps(last, d)) {
      // Coexist only when the overlap is partial. If one detection
      // fully contains the other, the contained one is redundant —
      // the wider span paints over the same characters — and keeping
      // both clutters the sidebar with duplicate entries on the same
      // address / name. Drop the contained one in that case.
      if (shouldCoexist(last, d) && !containsFully(last, d) && !containsFully(d, last)) {
        out.push(d);
      } else {
        out[lastIdx] = better(last, d);
      }
    } else {
      out.push(d);
    }
  }
  return out;
};

/**
 * Combine regex and NER detection lists, then dedupe with the cross-source
 * preference rules above. The order of inputs doesn't matter.
 */
export const mergeRegexAndNer = (
  regex: readonly TextDetection[],
  ner: readonly TextDetection[],
): TextDetection[] => mergeDetections([...regex, ...ner]);

/**
 * Additive NER pass. Returns the original detections as-is, plus any NER
 * detections that don't overlap an existing detection. Use this from the
 * background enrichment pass when the wave-1 redactions look correct
 * already and we only want NER to fill in gaps — never replace a hit
 * the regex / forms / dictionary pass already made.
 *
 * NER spans frequently bleed into adjacent tokens (the model treats
 * trailing punctuation, label words, and surrounding sentence context as
 * part of the entity). Even after the merge tiebreaks are tuned to
 * prefer regex bounds, an NER detection that REPLACES a wider regex
 * detection — or fragments it across multiple shorter NER spans —
 * leaves the user staring at a "wave 2 just broke everything up"
 * artifact. Only-add semantics avoid that entire failure mode.
 */
export const augmentWithNer = (
  existing: readonly TextDetection[],
  ner: readonly TextDetection[],
): TextDetection[] => {
  const additions = ner.filter((n) => !existing.some((e) => overlaps(e, n)));
  return mergeDetections([...existing, ...additions]);
};
